import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { echo, echoIsConfigured } from "@laravel/echo-react";
import { notify } from "@/lib/notify";
import { MessageSquare, X } from "lucide-react";
import { sound } from "@/lib/sound";
import { useAuthStore } from "@/store/auth.store";
import { messagingKeys, useConversations } from "@/hooks/useMessaging";
import { CHANNELS, EVENTS } from "@/config/echo-channels";
import { router } from "@/router";
import type { ApiConversation, ApiMessage } from "@/types";
import { getClassroomsList } from "@/services/classes.service";
import { useMessagingStore } from "@/store/messaging.store";

interface MessageSentEvent {
  conversation_id: number;
  message: ApiMessage;
  sender_id: number;
  timestamp: number | null;
}

function getClassUrl(conv: ApiConversation, role: string): string | null {
  const base =
    role === "admin"
      ? "/admin"
      : role === "teacher"
        ? "/teacher"
        : role === "family"
          ? "/parent"
          : "/student";

  if (role === "admin" && conv.class_id) return `${base}/classes/${conv.class_id}`;
  const uuid = conv.class_uuid ?? conv.class_id;
  if (uuid) return `${base}/classes/${uuid}`;
  return null;
}

function ClassroomToast({
  convTitle,
  senderName,
  toastId,
  onNavigate,
}: {
  convTitle: string;
  senderName: string;
  toastId: string | number;
  onNavigate?: () => void;
}) {
  return (
    <div
      style={{ background: "#1e3a5f" }}
      onClick={
        onNavigate
          ? () => {
              onNavigate();
              notify.dismiss(toastId);
            }
          : undefined
      }
      className={`flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-2xl w-[340px] border border-white/10${onNavigate ? " cursor-pointer hover:brightness-110 transition-all" : ""}`}
    >
      <div
        style={{ color: "#60a5fa", background: "#60a5fa20" }}
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
      >
        <MessageSquare size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-black text-sm leading-tight truncate">{convTitle}</p>
        <p className="text-white/60 text-xs font-medium mt-0.5 leading-snug">
          Nouveau message de <span className="text-white/80 font-bold">{senderName}</span>
        </p>
        {onNavigate && (
          <p className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-1.5">
            Cliquer pour voir →
          </p>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          notify.dismiss(toastId);
        }}
        className="shrink-0 mt-0.5 p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function useClassroomMessageChannels() {
  const userId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.role);
  const token = useAuthStore((s) => s.token);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  const qc = useQueryClient();
  const incrementUnread = useMessagingStore((s) => s.incrementUnread);

  const { data: conversations = [] } = useConversations();

  const isClassroomsNeeded = role === "admin" || role === "family";
  const { data: classrooms = [] } = useQuery({
    queryKey: ["classrooms-list"],
    queryFn: ({ signal }) => getClassroomsList(signal),
    enabled: !!token && emailVerified && isClassroomsNeeded,
  });

  const classroomConvs = useMemo(() => {
    const list = conversations.filter((c) => c.type === "classroom_chat");
    if (role === "admin" || role === "family") {
      classrooms.forEach((classroom) => {
        if (
          classroom.conversation_id &&
          !classroom.is_archived &&
          classroom.is_active &&
          !list.some((c) => c.id === classroom.conversation_id)
        ) {
          list.push({
            id: classroom.conversation_id,
            code: `CLASSROOM_CHAT_${classroom.id}`,
            type: "classroom_chat",
            title: classroom.name,
            description: classroom.description || null,
            is_archived: false,
            subject_id: null,
            class_id: classroom.id,
            class_uuid: classroom.uuid,
            last_message_at: classroom.created_at || new Date().toISOString(),
            last_message: null,
            unread_count: 0,
            participants: classroom.primary_teacher
              ? [
                  {
                    id: classroom.primary_teacher.id,
                    name: classroom.primary_teacher.name || "",
                    email: classroom.primary_teacher.email || "",
                    profile: "enseignant",
                  },
                ]
              : [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as ApiConversation);
        }
      });
    }
    return list;
  }, [conversations, classrooms, role]);

  // stable dep — resync subscriptions only when the set of IDs actually changes
  const convIdsKey = useMemo(() => {
    return classroomConvs
      .map((c) => c.id)
      .sort()
      .join(",");
  }, [classroomConvs]);

  /**
   * IDs actuellement abonnés côté Echo, en dehors du cycle de render.
   *
   * Sans ce suivi, chaque changement de `convIdsKey` — même l'ajout d'une seule classe — faisait
   * `leave` puis `listen` sur la totalité des conversations. Pour un admin (118 classes actives à
   * ce jour, sans plafond ni pagination sur `/v1/classrooms/list`), ça veut dire jusqu'à 118 appels
   * d'autorisation de canal privé relancés d'un coup pour un seul changement. Un échec ou un
   * timeout d'autorisation sur l'un d'eux — plausible à ce volume, sous charge backend — laissait
   * cette classe muette jusqu'au prochain remontage complet, d'où le symptôme « je ne reçois pas le
   * message, je dois rafraîchir » côté admin, absent chez un élève qui n'a qu'un canal.
   */
  const subscribedIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!userId || !echoIsConfigured()) return;

    const echoInst = echo();
    const subscribed = subscribedIdsRef.current;
    const currentIds = new Set(classroomConvs.map((c) => c.id));

    // Conversations disparues (classe archivée/désactivée, etc.) : désabonnement immédiat, peu
    // fréquent et sans effet notable sur la charge du backend.
    subscribed.forEach((id) => {
      if (!currentIds.has(id)) {
        echoInst.leave(CHANNELS.conversation(id));
        subscribed.delete(id);
      }
    });

    const toSubscribe = classroomConvs.filter((conv) => !subscribed.has(conv.id));

    /**
     * Étale les nouveaux abonnements par petits lots plutôt que tous à la fois.
     *
     * `private()` déclenche un appel d'autorisation HTTP (`/broadcasting/auth`) par canal — en
     * envoyer 118 d'un coup au premier montage admin risque de saturer/ralentir le backend et de
     * faire échouer certaines autorisations sans retry. Un lot de 10 toutes les 150 ms reste
     * imperceptible pour l'utilisateur (moins de 2 s pour 118 classes) tout en gardant le backend
     * sous une charge raisonnable.
     */
    const BATCH_SIZE = 10;
    const BATCH_DELAY_MS = 150;
    let cancelled = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const subscribeOne = (conv: (typeof classroomConvs)[number]) => {
      subscribed.add(conv.id);
      echoInst
        .private(CHANNELS.conversation(conv.id))
        .listen(EVENTS.conversation.messageSent, (event: MessageSentEvent) => {
          if (event.sender_id === userId) return;

          // Live-update the message cache (same dedup as useMessageChannel)
          qc.setQueryData<ApiMessage[]>(messagingKeys.messages(conv.id), (prev = []) =>
            prev.some((m) => m.id === event.message.id) ? prev : [...prev, event.message],
          );

          // Update local unread tracker
          incrementUnread(conv.id, event.message);

          // Un message de classe ne change que le « dernier message » de sa ligne : on le patche
          // dans le cache plutôt que de refetcher toute la liste, qui s'ajoutait au refetch déjà
          // déclenché par `unreadCountUpdated` et alimentait les 429 sur `/v1/conversations`. Le
          // compteur de non-lus reste à la charge de `unreadCountUpdated`. Le refetch ne subsiste
          // que pour une conversation absente du cache (liste d'un admin/parent pas encore chargée).
          const conversationsKey = [...messagingKeys.conversations, userId];
          const cached = qc.getQueryData<ApiConversation[]>(conversationsKey);
          if (cached?.some((c) => c.id === conv.id)) {
            qc.setQueryData<ApiConversation[]>(conversationsKey, (prev) =>
              prev?.map((c) =>
                c.id === conv.id
                  ? {
                      ...c,
                      last_message: event.message,
                      last_message_at: event.message.created_at,
                    }
                  : c,
              ),
            );
          } else {
            qc.invalidateQueries({ queryKey: messagingKeys.conversations });
          }

          // Trigger notifications (toast/sound) for all roles, including admin,
          // so new classroom messages surface live and bump to the top of the list.
          sound.notification();
          const classUrl = getClassUrl(conv, role);

          notify.custom(
            (t) => (
              <ClassroomToast
                convTitle={conv.title}
                senderName={event.message.user.name}
                toastId={t}
                onNavigate={classUrl ? () => router.navigate(classUrl) : undefined}
              />
            ),
            { duration: 5000 },
          );
        });
    };

    for (let i = 0; i < toSubscribe.length; i += BATCH_SIZE) {
      const batch = toSubscribe.slice(i, i + BATCH_SIZE);
      const delay = (i / BATCH_SIZE) * BATCH_DELAY_MS;
      timeouts.push(
        setTimeout(() => {
          if (!cancelled) batch.forEach(subscribeOne);
        }, delay),
      );
    }

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, convIdsKey, role, incrementUnread]);

  // Désabonnement complet au démontage (changement de compte, fermeture d'onglet…).
  useEffect(() => {
    return () => {
      if (!echoIsConfigured()) return;
      const echoInst = echo();
      subscribedIdsRef.current.forEach((id) => echoInst.leave(CHANNELS.conversation(id)));
      subscribedIdsRef.current.clear();
    };
  }, []);
}
